import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Users, ArrowUpRight, Briefcase, BookOpen, Heart, Clock } from "lucide-react";
import { FadeInOnScroll, StaggerChildren, StaggerItem, AnimatedDivider, HoverCard, ParallaxElement } from "@/components/animations/AnimationComponents";
import CTABand from "@/components/recruit/CTABand";

const IMAGES = {
  interior1: "https://s3.eu-central-1.amazonaws.com/assets.brease.io/media/sit-9f937fca-b3cf-4da3-8745-c6f91aa09f99/a0124f1d-e8be-4338-bae2-5fb36b0d9aa6.webp",
  interior2: "https://s3.eu-central-1.amazonaws.com/assets.brease.io/media/sit-9f937fca-b3cf-4da3-8745-c6f91aa09f99/a0124f34-31e6-4303-bfd2-ec31fe38edfe.webp",
  brand: "https://s3.eu-central-1.amazonaws.com/assets.brease.io/media/sit-9f937fca-b3cf-4da3-8745-c6f91aa09f99/a0389a5b-acd9-421c-a0b1-ef91b51311c7.webp",
  building: "https://customer-assets.emergentagent.com/job_21a5e501-0b8f-4c90-82cf-519f8b25a6ce/artifacts/h48mu8rg_a09b829a-6208-48d5-a18b-088067e18502.webp",
  buildingModern: "https://customer-assets.emergentagent.com/job_21a5e501-0b8f-4c90-82cf-519f8b25a6ce/artifacts/6efc2d6e_Untitled%20%283000%20x%202000%20px%29%20%282000%20x%203000%20px%29.jpg",
  heroLogo: "https://customer-assets.emergentagent.com/job_21a5e501-0b8f-4c90-82cf-519f8b25a6ce/artifacts/j4lh2ekn_ACOE_45x45.jpg",
};

const STATS = [
  { end: 102, decimals: 0, unit: "室", label: "運営客室数" },
  { end: 4.96, decimals: 2, unit: "", label: "Airbnb 平均評価（2025年）", sub: "市場平均 4.8" },
  { end: 96.9, decimals: 1, unit: "%", label: "総合品質スコア", sub: "市場平均 85.1%" },
  { end: 7, decimals: 0, unit: "年", label: "運営実績" },
  { end: 2019, decimals: 0, unit: "年", label: "創業", noAnimate: true },
  { end: 120, decimals: 0, unit: "日", label: "年間休日", sub: "月平均残業 20時間以内" },
];

function useCountUp(end, decimals = 0, duration = 2000, noAnimate = false) {
  const [value, setValue] = useState(noAnimate ? end : 0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStarted(true); },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started || noAnimate) return;
    const steps = duration / 16;
    const increment = end / steps;
    let current = 0;
    let frame;
    const tick = () => {
      current += increment;
      if (current >= end) {
        setValue(end);
      } else {
        setValue(Number(current.toFixed(decimals)));
        frame = requestAnimationFrame(tick);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [started, end, decimals, duration, noAnimate]);

  const display = noAnimate ? end.toString() : value.toFixed(decimals);
  return { display, ref };
}

function StatItem({ stat, index }) {
  const { display, ref } = useCountUp(stat.end, stat.decimals, 2200, stat.noAnimate);
  return (
    <div
      ref={ref}
      className="relative group"
      data-testid={`stat-card-${index}`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="h-px w-full bg-acoe-charcoal/20 mb-6 group-hover:bg-acoe-gold transition-colors duration-500" />
      <p className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl text-acoe-charcoal leading-none tracking-tight tabular-nums">
        {stat.prefix || ""}{display}<span className="text-xl md:text-2xl font-semibold text-acoe-gold ml-1">{stat.unit}</span>
      </p>
      <p className="mt-3 text-sm text-acoe-muted font-heading font-medium tracking-wide">{stat.label}</p>
      {stat.sub && <p className="mt-1.5 text-xs text-acoe-gold font-heading tracking-wide">{stat.sub}</p>}
    </div>
  );
}

const WHY_ITEMS = [
  {
    icon: TrendingUp,
    title: "勝てるプロダクト",
    desc: "一般的な民泊より高品質、ホテルよりも広く、完全プライベートな空間。Airbnb 4.96（2025年）、Booking.com 9.5（ACOE Asakusa）、品質スコア96.9%。60日以内に長期賃貸比250%の収益達成保証。オーナーに提案するのは可能性ではなく、実証済みの結果です。",
  },
  {
    icon: Users,
    title: "柔軟な働き方で、成果に集中",
    desc: "リモートワーク中心、出社は週2回、勤務時間もフレキシブル。カルチャーを大切にしながら結果を出す。優秀な人が自分らしく力を発揮できる、柔軟な環境を一緒に作っていきます。",
  },
  {
    icon: ArrowUpRight,
    title: "見える成長パス",
    desc: "新規事業フェーズの今、成果は報酬と役職に直結。インセンティブは上限なし、将来の役員登用も視野に入るポジションです。",
  },
];

const NAV_PATHS = [
  { icon: Briefcase, title: "会社を知りたい", desc: "ACOEの事業・実績・成り立ちを詳しく", path: "/recruit/about" },
  { icon: BookOpen, title: "仕事を知りたい", desc: "物件開拓営業の具体的な仕事内容", path: "/recruit/work" },
  { icon: Heart, title: "文化を知りたい", desc: "働き方・評価制度・成長環境", path: "/recruit/culture" },
  { icon: Clock, title: "3分で知りたい", desc: "忙しい方向けの全体サマリー", path: "/recruit/summary" },
];

export default function TopPage() {
  useEffect(() => { document.title = "ACOE 採用サイト | Recruit"; }, []);

  return (
    <div data-testid="top-page">
      {/* ─── HERO ─── */}
      <section className="relative min-h-screen flex items-center bg-acoe-charcoal overflow-hidden" data-testid="hero-section">
        {/* Subtle grain texture overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'1\'/%3E%3C/svg%3E")' }} />

        <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-12 py-32 md:py-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left: Text content */}
            <div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-acoe-gold text-sm tracking-[0.25em] uppercase font-heading font-semibold mb-8"
              >
                Franchise Development Sales
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4 }}
                className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.15]"
              >
                一棟の可能性を、<br />共に拓く。
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="mt-8 text-white/60 text-base md:text-lg max-w-lg leading-relaxed font-body"
              >
                ACOEは東京でファミリー向けライフスタイル宿泊ブランドを展開する成長企業です。
                次のフェーズを共に創る、物件開拓営業を募集しています。
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="mt-12 flex flex-col sm:flex-row gap-4"
              >
                <Link
                  to="/recruit/contact"
                  className="inline-flex items-center justify-center rounded-full bg-acoe-gold text-acoe-charcoal px-8 py-4 font-heading font-semibold tracking-wide hover:bg-acoe-gold-hover transition-colors"
                  data-testid="hero-cta-primary"
                >
                  お問い合わせ
                </Link>
                <Link
                  to="/recruit/careers"
                  className="inline-flex items-center justify-center rounded-full border border-white/25 text-white px-8 py-4 font-heading font-semibold tracking-wide hover:bg-white/10 transition-all"
                  data-testid="hero-cta-secondary"
                >
                  募集要項を見る
                </Link>
              </motion.div>
            </div>

            {/* Right: Hero image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="hidden lg:flex items-center justify-center"
            >
              <img
                src={IMAGES.heroLogo}
                alt="ACOE"
                className="w-[85%] max-w-[480px] select-none rounded-3xl shadow-2xl"
                draggable="false"
              />
            </motion.div>
          </div>
        </div>

        {/* Bottom edge line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-acoe-gold/30 to-transparent" />
      </section>

      {/* ─── STATS ─── */}
      <section className="py-24 md:py-36 bg-acoe-bg relative overflow-hidden" data-testid="stats-section">
        {/* Decorative ACOE watermark */}
        <ParallaxElement speed={0.3} className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
          <img src="/acoe_logo_black.png" alt="" className="w-[500px]" draggable="false" />
        </ParallaxElement>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeInOnScroll>
            <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">
              ACOE in Numbers
            </p>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-4">
              数字で見るACOE
            </h2>
            <AnimatedDivider className="w-16 mb-16 md:mb-20" />
          </FadeInOnScroll>
          <StaggerChildren className="grid grid-cols-2 md:grid-cols-3 gap-x-8 md:gap-x-16 gap-y-12 md:gap-y-16" staggerDelay={0.1}>
            {STATS.map((stat, i) => (
              <StaggerItem key={i}>
                <StatItem stat={stat} index={i} />
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* ─── FOUNDER PREVIEW ─── */}
      <section className="py-20 md:py-32 bg-acoe-paper" data-testid="founder-preview-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <FadeInOnScroll direction="left">
              <div>
                <div className="aspect-square overflow-hidden">
                  <img
                    src={IMAGES.building}
                    alt="ACOE Higashi-Shinjuku"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <p className="text-xs text-acoe-muted mt-3 font-heading">ACOE Higashi-Shinjuku</p>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll direction="right" delay={0.2}>
              <div>
                <p className="text-sm tracking-widest uppercase text-acoe-muted font-heading font-semibold mb-4">
                  Founder Message
                </p>
                <h2 className="font-accent italic text-2xl md:text-3xl text-acoe-charcoal leading-snug mb-6">
                  「この仲間と、次の100棟を。」
                </h2>
                <p className="text-acoe-text leading-relaxed mb-4">
                  ACOEはこれまで代表自ら物件を見つけ、交渉し、運営してきました。
                  6年間で14棟・102室。すべて実地で積み上げた運営ノウハウがあります。
                </p>
                <p className="text-acoe-text leading-relaxed mb-8">
                  次のフェーズでは、同じ目線で事業を動かせるパートナーを求めています。
                  営業の力で、ACOEの次の成長を共に創りませんか。
                </p>
                <Link
                  to="/recruit/message"
                  className="inline-flex items-center gap-2 text-acoe-charcoal font-heading font-semibold hover:text-acoe-gold transition-colors group"
                  data-testid="founder-preview-link"
                >
                  代表メッセージを読む
                  <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
                </Link>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* ─── WHY ACOE ─── */}
      <section className="py-20 md:py-32 bg-acoe-bg" data-testid="why-acoe-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeInOnScroll>
            <p className="text-sm tracking-widest uppercase text-acoe-muted font-heading font-semibold mb-3">
              Why ACOE
            </p>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-4">
              なぜACOEか
            </h2>
            <AnimatedDivider className="w-16 mb-12" />
          </FadeInOnScroll>
          <StaggerChildren className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" staggerDelay={0.15}>
            {WHY_ITEMS.map((item, i) => {
              const Icon = item.icon;
              return (
                <StaggerItem key={i}>
                  <HoverCard className="bg-white border border-acoe-border p-8 md:p-10 group h-full" data-testid={`why-card-${i}`}>
                    <div className="w-12 h-12 rounded-full bg-acoe-paper flex items-center justify-center mb-6 group-hover:bg-acoe-gold/10 transition-colors">
                      <Icon size={22} className="text-acoe-charcoal" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-heading font-bold text-lg text-acoe-charcoal mb-3">{item.title}</h3>
                    <p className="text-sm text-acoe-text leading-relaxed">{item.desc}</p>
                  </HoverCard>
                </StaggerItem>
              );
            })}
          </StaggerChildren>
        </div>
      </section>

      {/* ─── ROLE PREVIEW ─── */}
      <section className="py-20 md:py-32 bg-acoe-charcoal" data-testid="role-preview-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <FadeInOnScroll direction="left">
              <div>
                <p className="text-sm tracking-widest uppercase text-acoe-gold font-heading font-semibold mb-4">
                  Now Hiring
                </p>
                <h2 className="font-heading font-bold text-2xl md:text-4xl text-white mb-6">
                  物件開拓営業とは
                </h2>
                <p className="text-white/70 leading-relaxed mb-4">
                  ヒルトンやマリオットが大規模ホテルで実現しているブランド運営モデルを、東京の小規模物件で展開する。
                  ACOEが目指しているのは、「最も愛されるファミリー向けホスピタリティブランド」です。その成長の鍵を握るのが、この物件開拓営業というポジションです。
                </p>
                <p className="text-white/70 leading-relaxed mb-4">
                  ACOEブランドで運営できる物件の候補を見つけ、パイプラインを広げていくのが主な仕事です。
                  直接のアプローチでも、外部パートナーの活用でも、方法はあなた次第。オーナーとの面談を設定し、提案を重ね、契約後も良い関係を続けていく。一つひとつの物件が、ブランドの成長そのものになります。
                </p>
                <p className="text-white/70 leading-relaxed mb-8">
                  代表と直接並走しながら進められるので、判断に迷うことがあっても一人で抱え込む必要はありません。
                  自分のペースで市場を開拓し、ACOEの成長をリアルタイムで実感できるポジションです。
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/recruit/work"
                    className="inline-flex items-center justify-center rounded-full bg-acoe-gold text-acoe-charcoal px-8 py-4 font-heading font-semibold tracking-wide hover:bg-acoe-gold-hover transition-colors"
                    data-testid="role-preview-link"
                  >
                    仕事の詳細を見る
                </Link>
                <Link
                  to="/recruit/contact"
                  className="inline-flex items-center justify-center rounded-full border border-white/30 text-white px-8 py-4 font-heading font-semibold tracking-wide hover:bg-white/10 transition-all"
                >
                  お問い合わせ
                </Link>
              </div>
            </div>
            </FadeInOnScroll>
            <FadeInOnScroll direction="right" delay={0.2}>
              <div className="flex flex-col items-center">
                <div className="overflow-hidden">
                  <img
                    src={IMAGES.buildingModern}
                    alt="ACOE Asakusa"
                    className="w-auto max-h-[500px] object-contain hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <p className="text-xs text-white/50 mt-3 font-heading w-full max-w-[333px] text-right">ACOE Asakusa</p>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* ─── NAVIGATION PATHS ─── */}
      <section className="py-20 md:py-32 bg-acoe-bg" data-testid="nav-paths-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeInOnScroll>
            <p className="text-sm tracking-widest uppercase text-acoe-muted font-heading font-semibold mb-3">
              Explore
            </p>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-4">
              あなたの知りたいことから
            </h2>
            <AnimatedDivider className="w-16 mb-12" />
          </FadeInOnScroll>
          <StaggerChildren className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" staggerDelay={0.1}>
            {NAV_PATHS.map((item, i) => {
              const Icon = item.icon;
              return (
                <StaggerItem key={i}>
                  <Link
                    to={item.path}
                    className="block h-full"
                    data-testid={`nav-path-${i}`}
                  >
                    <HoverCard className="bg-white border border-acoe-border p-6 md:p-8 group flex flex-col h-full">
                      <Icon size={22} className="text-acoe-muted mb-4 group-hover:text-acoe-gold transition-colors" strokeWidth={1.5} />
                      <h3 className="font-heading font-bold text-base text-acoe-charcoal mb-2">{item.title}</h3>
                      <p className="text-sm text-acoe-text leading-relaxed flex-1">{item.desc}</p>
                      <ArrowUpRight
                        size={18}
                        className="mt-4 text-acoe-muted group-hover:text-acoe-charcoal group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                        strokeWidth={1.5}
                      />
                    </HoverCard>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerChildren>
        </div>
      </section>

      {/* ─── CTA BAND ─── */}
      <CTABand variant="dark" />
    </div>
  );
}
